type PendingRequest = {
  resolve: (result: IUsaddressTagResult) => void;
  reject: (error: Error) => void;
};

/** Spawns public/usaddress/worker.js, pointing it at the copied Pyodide runtime */
export const createUsaddressWorker = () => {
  const base = import.meta.env.BASE_URL;
  const params = new URLSearchParams({
    pyodide: `${base}${PYODIDE_PATH}`,
    v: APP_VERSION,
  });
  return new Worker(`${base}usaddress/worker.js?${params}`, {
    type: "module",
  });
};

/**
 * Talks to the Pyodide worker that runs the Python `usaddress` package.
 * The worker (and its ~13MB WASM runtime) is only spawned on first use.
 */
export const UsaddressProviderFactory = ({
  createWorker,
}: {
  createWorker: () => Worker;
}): IUsaddressProvider => {
  let worker: Worker | null = null;
  let nextId = 0;
  const pending = new Map<number, PendingRequest>();

  const rejectAll = (error: Error) => {
    pending.forEach(({ reject }) => reject(error));
    pending.clear();
  };

  const getWorker = () => {
    if (worker) return worker;

    worker = createWorker();
    worker.onmessage = (event: MessageEvent<UsaddressWorkerResponse>) => {
      const request = pending.get(event.data.id);
      if (!request) return;
      pending.delete(event.data.id);
      if ("error" in event.data) {
        request.reject(new Error(event.data.error));
      } else {
        request.resolve(event.data.result);
      }
    };
    worker.onerror = (event) => {
      event.preventDefault();
      worker?.terminate();
      worker = null;
      rejectAll(new Error(event.message || "Address parser failed to load"));
    };
    return worker;
  };

  const tag: IUsaddressProvider["tag"] = (address) =>
    new Promise((resolve, reject) => {
      const id = ++nextId;
      pending.set(id, { resolve, reject });
      getWorker().postMessage({ id, address } as UsaddressWorkerRequest);
    });

  return { tag };
};
